import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { extraDayMatchesFilters, hourBranchId } from "./scheduleAvailability";
import { resolveScheduleConfigForPortal } from "./resolveScheduleConfig";
dayjs.extend(utc);
dayjs.extend(timezone);

const MS_PER_MINUTE = 60 * 1000;

function toValidMs(value) {
  if (value == null || value === "") return null;
  const ms = dayjs(value).valueOf();
  return Number.isFinite(ms) ? ms : null;
}

function positiveMinutes(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Intervalo ocupado [start, end). Misma regla que el server:
 * hourEnd > hourStart, si no sizeSlot del shift, si no fallback del día.
 */
function occupiedIntervalMs(shift, fallbackDurationMinutes) {
  const startMs = toValidMs(shift?.hourStart);
  if (startMs == null) return null;

  const endMs = toValidMs(shift?.hourEnd);
  if (endMs != null && endMs > startMs) {
    return { startMs, endMs };
  }

  const duration =
    positiveMinutes(shift?.sizeSlot) ||
    positiveMinutes(fallbackDurationMinutes);
  if (!duration) return null;

  return { startMs, endMs: startMs + duration * MS_PER_MINUTE };
}

function slotIntervalMs(slot, fallbackDurationMinutes) {
  const startMs = toValidMs(slot?.hourStart);
  if (startMs == null) return null;

  const duration =
    positiveMinutes(slot?.sizeSlot) || positiveMinutes(fallbackDurationMinutes);
  if (!duration) return null;

  return { startMs, endMs: startMs + duration * MS_PER_MINUTE };
}

function intervalsOverlap(a, b) {
  if (!a || !b) return false;
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

function isShiftOccupyingSlot(shift, slot, daySizeSlot) {
  if (!shift || shift.softDelete) return false;
  return intervalsOverlap(
    occupiedIntervalMs(shift, daySizeSlot),
    slotIntervalMs(slot, daySizeSlot),
  );
}

export const generateSlots = async ({
  sizeSlot,
  turns,
  userTurn,
  date,
  tenantTimezone,
  holidays = [],
  branches = [],
  selectedServiceId = null,
  selectedBranchId = null,
}) => {
  let generate = [];
  let minutesOfHour = 60;
  let day = dayjs(date).format("dd");

  let configSchedule = {
    excludeDays: [],
    lu: {},
    ma: {},
    mi: {},
    ju: {},
    vi: {},
    sá: {},
    do: {},
  };

  const todayStart = dayjs().tz(tenantTimezone).startOf("day");
  const dateStart = dayjs(date).tz(tenantTimezone).startOf("day");
  const quantityDays = userTurn?.scheduleConfig?.quantityDaysPortal || 15;

  if (dateStart.diff(todayStart, "days") > quantityDays) {
    return false;
  }

  // Verificar si la fecha es un feriado
  const isHoliday = holidays?.some(
    (holiday) =>
      holiday.year === dayjs(date).year() &&
      holiday.month === dayjs(date).month() + 1 &&
      holiday.day === dayjs(date).date(),
  );

  let usualConfigSchedule = false;

  const updateConfigSchedule = (scheduleConfig, variable) => {
    scheduleConfig?.hours?.forEach((h, index) => {
      const start = dayjs(scheduleConfig.hours[index]?.start).utc();
      const end = dayjs(scheduleConfig.hours[index]?.end).utc();
      const blockSize = h?.sizeSlot || null; // null → usa el sizeSlot global

      h?.days?.forEach((e) => {
        const entriesLength = Object.entries(variable[e]).length;
        variable[e] =
          entriesLength === 0
            ? { first: [start, end, blockSize] }
            : entriesLength === 1
              ? { ...variable[e], second: [start, end, blockSize] }
              : { ...variable[e] };
      });
    });
  };

  const isPastDate = dateStart.isBefore(todayStart);

  // extraDay tiene prioridad absoluta: override explícito de horario para ese día.
  // Fechas pasadas sin extraDay: usar el scheduleConfig histórico guardado en el Turn si tiene turnos.
  // Hoy/futuro sin extraDay: usar la configuración semanal actual del médico.
  let configToUse = resolveScheduleConfigForPortal({
    dayTurns: turns,
    userScheduleConfig: userTurn?.scheduleConfig,
    branches,
    date,
    tenantTimezone,
  });

  // Filtro por sede DESPUÉS del merge/enriquecimiento (snapshot + live).
  // Así el enriquecimiento de branchId usa el live completo, no solo la sede filtrada.
  if (
    selectedBranchId &&
    selectedBranchId !== "all" &&
    configToUse?.hours?.length
  ) {
    const bid = String(selectedBranchId);
    configToUse = {
      ...configToUse,
      hours: configToUse.hours.filter((h) => hourBranchId(h) === bid),
    };
  }

  // perBlock: filtrar bloques DESPUÉS del merge (snapshot horarios + live servicios)
  const isPerBlock = userTurn?.scheduleConfig?.serviceConfigMode === "perBlock";
  if (selectedServiceId && isPerBlock && configToUse?.hours?.length) {
    configToUse = {
      ...configToUse,
      hours: configToUse.hours.filter((h) =>
        (h.serviceIds || []).some(
          (sid) => String(sid) === String(selectedServiceId),
        ),
      ),
    };
  }

  await updateConfigSchedule(configToUse, configSchedule);
  usualConfigSchedule = {
    excludeDays: [],
    lu: {},
    ma: {},
    mi: {},
    ju: {},
    vi: {},
    sá: {},
    do: {},
  };
  await updateConfigSchedule(userTurn?.scheduleConfig, usualConfigSchedule);

  let hours =
    configSchedule[day]?.first !== undefined &&
    configSchedule[day]?.first[0].format("H");

  let minutes =
    configSchedule[day]?.first !== undefined &&
    configSchedule[day]?.first[0].format("mm");

  let hourStart = hours && dayjs(date).startOf("day").add(hours, "h");

  hourStart = hourStart && hourStart.add(minutes, "m");

  // Tamaño de slot efectivo por bloque (fallback al global si no está definido)
  const sizeSlot1 = configSchedule[day]?.first?.[2] || sizeSlot;
  const sizeSlot2 = configSchedule[day]?.second?.[2] || sizeSlot;

  let res1 =
    hourStart && configSchedule[day]?.first !== undefined
      ? configSchedule[day]?.first[1].diff(
          configSchedule[day]?.first[0],
          "minute",
        ) / minutesOfHour
      : 0;

  let q1 = (minutesOfHour / sizeSlot1) * res1;

  let res2 =
    hourStart && configSchedule[day]?.second !== undefined
      ? configSchedule[day]?.second[1].diff(
          configSchedule[day]?.second[0],
          "minute",
        ) / minutesOfHour
      : 0;

  let q2 = res2 !== 0 ? Math.ceil((minutesOfHour / sizeSlot2) * res2) + 1 : 0;

  const isDateExcluded = userTurn?.scheduleConfig?.excludeDays?.some((e) =>
    dayjs(e).tz(tenantTimezone).isSame(dayjs(date).tz(tenantTimezone), "day"),
  );

  // Bloquear generación de slots si es feriado (a menos que haya un extraDay o turnos)
  const shouldBlockHoliday =
    isHoliday && !turns?.extraDay && (!turns?.shifts?.length || !turns);

  if (!isDateExcluded && !shouldBlockHoliday) {
    const q1ceil = Math.ceil(q1);
    for (let i = 0; i < q1 + q2; i++) {
      const blockSize = i < q1ceil ? sizeSlot1 : sizeSlot2;
      generate[i] = {
        hourStart: dayjs
          .utc(hourStart.format("YYYY-MM-DDTHH:mm:ss"))
          .toISOString(),
        sizeSlot: blockSize,
      };

      if (i == q1ceil) {
        if (configSchedule[day]?.second !== undefined) {
          let hours = configSchedule[day]?.second[0].format("H");

          let minutes = configSchedule[day]?.second[0].format("mm");

          hourStart = dayjs(date).startOf("day").add(hours, "h");

          hourStart = hourStart.add(minutes, "m");

          generate[i] = {
            hourStart: hourStart
              .utc(hourStart.format("YYYY-MM-DDTHH:mm:ss"))
              .toISOString(),
            sizeSlot: sizeSlot2,
            exclude: true,
            second: true,
          };
          continue;
        }
      }

      // Avanzar con el tamaño del bloque actual
      hourStart = hourStart.add(blockSize, "m");
    }
  } else {
    generate[0] = {
      exclude: true,
    };
  }

  // Hoy o futuro, usando horario live y con slots disponibles:
  // si hay turnos asignados fuera del horario actual (ej. médico cerró más temprano),
  // los agregamos como slots extra en su posición por hora para que sigan visibles.
  // No reordenamos el array completo para no romper la división en bloques (exclude + second).
  if (
    !isPastDate &&
    configToUse === userTurn?.scheduleConfig &&
    turns?.shifts?.length > 0 &&
    Array.isArray(generate) &&
    generate.length > 0 &&
    !generate[0]?.exclude
  ) {
    const existingSlotMinutes = (generate || [])
      .filter((s) => s?.hourStart && !s?.exclude)
      .map((s) =>
        dayjs(s.hourStart).diff(dayjs(s.hourStart).startOf("day"), "minute"),
      );
    const extraSlots = [];
    (turns.shifts || []).forEach((shift) => {
      if (shift?.softDelete || !shift?.hourStart) return;
      const shiftMinutes = dayjs(shift.hourStart).diff(
        dayjs(shift.hourStart).startOf("day"),
        "minute",
      );
      const alreadyCovered = existingSlotMinutes.some(
        (slotMin) => Math.abs(slotMin - shiftMinutes) < sizeSlot,
      );
      if (!alreadyCovered) {
        extraSlots.push({
          hourStart: dayjs(shift.hourStart).utc().toISOString(),
          sizeSlot: positiveMinutes(shift.sizeSlot) || sizeSlot,
        });
        existingSlotMinutes.push(shiftMinutes);
      }
    });

    if (extraSlots.length > 0) {
      extraSlots.sort(
        (a, b) => dayjs(a.hourStart).valueOf() - dayjs(b.hourStart).valueOf(),
      );
      for (const extra of extraSlots) {
        const extraTime = dayjs(extra.hourStart).valueOf();
        const idx = (generate || []).findIndex(
          (s) => s?.hourStart && dayjs(s.hourStart).valueOf() > extraTime,
        );
        if (idx === -1) {
          generate.push(extra);
        } else {
          generate.splice(idx, 0, extra);
        }
      }
    }
  }

  const daySizeSlot = turns?.sizeSlot || sizeSlot;
  const updatedSlots = (generate || []).map((slot) => {
    const isOccupied = (turns?.shifts || []).some((shift) =>
      isShiftOccupyingSlot(shift, slot, daySizeSlot),
    );

    return { ...slot, occupied: isOccupied };
  });

  const filteredSlots = updatedSlots.filter((slot) => {
    return (
      !slot.occupied &&
      !slot?.exclude &&
      (dayjs().tz(tenantTimezone).add(2, "hours") <=
        dayjs(slot?.hourStart).tz(tenantTimezone) ||
        dayjs().tz(tenantTimezone) < dayjs(date).tz(tenantTimezone))
    );
  });

  return {
    usualConfigSchedule: usualConfigSchedule || configSchedule,
    configSchedule: configSchedule,
    filteredSlots,
    effectiveConfig: configToUse || null,
  };
};

export const disabledDate = ({
  current,
  excludedDaysMap,
  holidaysMap,
  configSchedule,
  scheduleConfig,
  allTurns,
  selectedBranchId = null,
  selectedServiceId = null,
  liveScheduleHours = null,
  calendarWeekSchedule = null,
}) => {
  if (!current) return false;

  const quantityDays = scheduleConfig?.quantityDaysPortal || 15;
  const isPastOrFuture =
    current.isBefore(dayjs().startOf("day")) ||
    current.startOf("day").diff(dayjs().startOf("day"), "days") > quantityDays;

  // Formato de la fecha para comparación
  const formattedDate = current.startOf("day").toISOString();

  // Verificaciones adicionales
  const isExcludedDay = excludedDaysMap.has(formattedDate);
  const dayKey = current.format("dd");
  const weekSchedule = calendarWeekSchedule || configSchedule;
  const isScheduledWeekday = Boolean(weekSchedule?.[dayKey]?.first);

  let data = allTurns?.find(
    (turn) =>
      dayjs(current).format("DD") == turn.day &&
      dayjs(current).format("MM") == turn.month &&
      dayjs(current).format("YYYY") == turn.year,
  );

  const liveHours = liveScheduleHours || scheduleConfig?.hours;
  const extraDayOk =
    data?.extraDay &&
    extraDayMatchesFilters({
      turn: data,
      liveHours,
      selectedBranchId,
      selectedServiceId,
    });

  const hasActiveShifts =
    data?.shifts?.length > 0 && data.shifts.some((s) => !s?.softDelete);
  const hasFreeSlots = (data?.freeSlots ?? 0) > 0;

  // Con servicio elegido (perBlock): solo días de la semana de ese servicio o extraDay que lo incluya
  const dayHasAvailability = selectedServiceId
    ? isScheduledWeekday || extraDayOk
    : isScheduledWeekday ||
      extraDayOk ||
      (hasFreeSlots && !data?.extraDay) ||
      hasActiveShifts;

  const disabledScheduleDay = !dayHasAvailability;

  return {
    isDisabled:
      isPastOrFuture ||
      (holidaysMap.has(
        `${current.year()}-${current.month() + 1}-${current.date()}`,
      ) &&
        !data?.extraDay) ||
      disabledScheduleDay ||
      isExcludedDay,
    freeSlots: data?.freeSlots,
  };
};
